import { useState, useMemo } from "react";
import { Rocket, DollarSign, TrendingUp, Users, Heart } from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";

import {
  Card,
  PageHeader,
  StatCard,
  Tabs,
  Progress,
} from "../adminComponents/adminComponents";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useAdminImpactHook } from "../../hooks/useAdminHooks";
import { useManagerImpactHook } from "../../hooks/useManagerHooks";
import "./impact.css";

const TABS = [
  { value: "ventures", label: "Ventures" },
  { value: "financing", label: "Financing" },
  { value: "revenue", label: "Revenue" },
  { value: "jobs", label: "Jobs" },
  { value: "qol", label: "Quality of Life" },
];

const STAGE_TONES = {
  seedling: "muted",
  sprout: "accent",
  sapling: "success",
  tree: "primary",
  redwood: "warning",
};

const ICONS_BY_NAME = {
  rocket: Rocket,
  dollar: DollarSign,
  "trending-up": TrendingUp,
  users: Users,
  heart: Heart,
};

const DEFAULT_IMPACT_DATA = {
  summary_cards: [],
  ventures: {
    survival_rates: [],
    stage_distribution: [],
  },
  financing: {
    capital_deployment: [],
    capital_effeciency: {
      leverage_ratio: { value: "0x" },
      first_time_access: { value: "0%" },
      avg_ticket_size: { value: "UGX 0" },
    },
  },
  revenue: {
    growth_rate: [],
    growth_buckets: [],
  },
  jobs: {
    jobs_distribution: [],
  },
  quality_of_life: {
    indicator_breakdown: [],
  },
};

export default function Impact() {
  const [tab, setTab] = useState("ventures");
  const { user, token } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;
  const isManager = user?.role === ROLES.MANAGER;

  const adminImpactQuery = useAdminImpactHook({
    token,
    userId: user?.id,
    enabled: isAdmin,
  });

  const managerImpactQuery = useManagerImpactHook({
    token,
    userId: user?.id,
    enabled: isManager,
  });

  const impactApiData = isManager
    ? managerImpactQuery.data
    : adminImpactQuery.data;
  const isLoading = isManager
    ? managerImpactQuery.isLoading
    : adminImpactQuery.isLoading;

  const impactData = impactApiData ?? DEFAULT_IMPACT_DATA;

  const summaryCards = impactData?.summary_cards ?? [];

  const survivalCurve = useMemo(
    () =>
      (impactData?.ventures?.survival_rates ?? []).map((point) => ({
        month: String(point.month ?? ""),
        rate: Number(point.rate ?? 0),
      })),
    [impactData?.ventures?.survival_rates],
  );

  const stageBreakdown = useMemo(
    () =>
      (impactData?.ventures?.stage_distribution ?? []).map((row) => ({
        stage: String(row.stage ?? "").toLowerCase(),
        label: row.stage ?? "-",
        count: Number(row.count ?? 0),
      })),
    [impactData?.ventures?.stage_distribution],
  );

  const totalStageCount =
    stageBreakdown.reduce((sum, row) => sum + row.count, 0) || 1;

  const capitalSeries = useMemo(
    () =>
      (impactData?.financing?.capital_deployment ?? []).map((row) => ({
        quarter: row.period ?? "-",
        deployed: Number(row.ih_capital ?? 0),
        leveraged: Number(row.external ?? 0),
      })),
    [impactData?.financing?.capital_deployment],
  );

  const capitalEfficiency = impactData?.financing?.capital_effeciency ?? {};

  // Revenue 3-month growth distribution bucketed for the 120+ ventures.
  const revenueGrowthBuckets = useMemo(
    () =>
      (impactData?.revenue?.growth_buckets ?? []).map((row) => ({
        bucket: row.bucket ?? "-",
        count: Number(row.count ?? 0),
      })),
    [impactData?.revenue?.growth_buckets],
  );

  // Jobs grouped by sector from backend impact payload.
  const jobsBySector = useMemo(() => {
    return (impactData?.jobs?.jobs_distribution ?? []).map((row) => ({
      sector: row.sector ?? "-",
      fullTime: Number(row.full_time ?? 0),
      partTime: Number(row.part_time ?? 0),
    }));
  }, [impactData?.jobs?.jobs_distribution]);

  // QoL radar — baseline vs midline values provided by backend.
  const qolRadar = useMemo(() => {
    return (impactData?.quality_of_life?.indicator_breakdown ?? []).map(
      (row) => ({
        indicator: row.indicator ?? "-",
        baseline: Number(row.baseline ?? 0),
        midline: Number(row.midline ?? 0),
        improvement: row.improvement ?? null,
      }),
    );
  }, [impactData?.quality_of_life?.indicator_breakdown]);

  if (isLoading && (isAdmin || isManager)) {
    return (
      <div className="adminImpact-root">
        <PageHeader
          title="Impact & Outcomes"
          description="Track the five core outcomes across your venture portfolio"
        />
        <p className="admin-muted">Loading impact data...</p>
      </div>
    );
  }

  return (
    <div className="adminImpact-root">
      <PageHeader
        title="Impact & Outcomes"
        description="Track the five core outcomes across your venture portfolio"
      />

      <div className="admin-grid admin-grid-5">
        {summaryCards.map((card, idx) => {
          const Icon = ICONS_BY_NAME[card.icon] ?? Rocket;
          return (
            <StatCard
              key={`${card.title}-${idx}`}
              title={card.title}
              value={card.value}
              description={card.label}
              icon={Icon}
            />
          );
        })}
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === "ventures" && (
        <div className="admin-grid admin-grid-2">
          <Card
            title="Survival Curve"
            description="Venture survival rates over time"
          >
            <div className="adminImpact-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={survivalCurve}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Area
                    type="monotone"
                    dataKey="rate"
                    stroke="hsl(181, 57%, 42%)"
                    fill="hsl(181, 57%, 42%)"
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card
            title="Stage Distribution"
            description="Current venture pipeline status"
          >
            <div className="adminImpact-stageList">
              {stageBreakdown.map((stageRow) => (
                <div key={stageRow.stage} className="adminImpact-stageRow">
                  <div className="adminImpact-stageHead">
                    <span>{stageRow.label}</span>
                    <strong>{stageRow.count} ventures</strong>
                  </div>
                  <Progress
                    value={(stageRow.count / totalStageCount) * 100}
                    tone={
                      STAGE_TONES[stageRow.stage] === "muted"
                        ? "primary"
                        : STAGE_TONES[stageRow.stage]
                    }
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === "financing" && (
        <div className="admin-grid admin-grid-2">
          <Card
            title="Capital Deployment vs Leverage"
            description="Her capital deployed and external capital leveraged"
          >
            <div className="adminImpact-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={capitalSeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="quarter" />
                  <YAxis tickFormatter={(value) => `${value}M`} />
                  <Tooltip
                    formatter={(value) => `UGX ${Number(value).toFixed(2)}M`}
                  />
                  <Legend />
                  <Bar
                    dataKey="deployed"
                    name="Her Capital"
                    fill="hsl(327, 72%, 42%)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="leveraged"
                    name="External"
                    fill="hsl(64, 53%, 46%)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card
            title="Leverage Metrics"
            description="Capital efficiency indicators"
          >
            <div className="adminImpact-leverage">
              <p className="admin-muted">Leverage Ratio</p>
              <p className="adminImpact-leverageValue">
                {capitalEfficiency?.leverage_ratio?.value ?? "0x"}
              </p>
              <p className="admin-muted adminImpact-leverageDesc">
                Capital leveraged for every UGX 1 deployed.
              </p>
            </div>
            <div className="admin-grid admin-grid-2">
              <div className="adminImpact-microStat">
                <p className="admin-muted">First-Time Access</p>
                <p className="adminImpact-microStatValue">
                  {capitalEfficiency?.first_time_access?.value ?? "0%"}
                </p>
                <p className="admin-muted">of ventures</p>
              </div>
              <div className="adminImpact-microStat">
                <p className="admin-muted">Avg. Ticket Size</p>
                <p className="adminImpact-microStatValue">
                  {capitalEfficiency?.avg_ticket_size?.value ?? "UGX 0"}
                </p>
                <p className="admin-muted">per venture</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === "revenue" && (
        <Card
          title="Revenue Growth Distribution"
          description="Ventures bucketed by 3-month revenue growth (current 3 months vs prior 3 months)"
        >
          <div className="adminImpact-chart adminImpact-chart-tall">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueGrowthBuckets}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value) => [`${value} ventures`, "Count"]}
                />
                <Bar
                  dataKey="count"
                  name="Ventures"
                  fill="hsl(181, 57%, 42%)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {tab === "jobs" && (
        <Card
          title="Jobs by Sector"
          description="Full-time and part-time job distribution"
        >
          <div className="adminImpact-chart adminImpact-chart-tall">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={jobsBySector}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="sector" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="fullTime"
                  name="Full-Time"
                  stackId="a"
                  fill="hsl(327, 72%, 42%)"
                />
                <Bar
                  dataKey="partTime"
                  name="Part-Time"
                  stackId="a"
                  fill="hsl(327, 72%, 65%)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {tab === "qol" && (
        <div className="admin-grid admin-grid-2">
          <Card
            title="Quality of Life Indicators"
            description="Baseline vs midline comparison"
          >
            <div className="adminImpact-chart adminImpact-chart-tall">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={qolRadar}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="indicator" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  <Radar
                    name="Baseline"
                    dataKey="baseline"
                    stroke="hsl(327, 72%, 42%)"
                    fill="hsl(327, 72%, 42%)"
                    fillOpacity={0.3}
                  />
                  <Radar
                    name="Midline"
                    dataKey="midline"
                    stroke="hsl(181, 57%, 42%)"
                    fill="hsl(181, 57%, 42%)"
                    fillOpacity={0.3}
                  />
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card
            title="Indicator Breakdown"
            description="Improvement across QoL dimensions"
          >
            <div className="adminImpact-qolList">
              {qolRadar.map((item) => {
                const delta = item.midline - item.baseline;
                return (
                  <div key={item.indicator} className="adminImpact-qolRow">
                    <div className="adminImpact-qolHead">
                      <span>{item.indicator}</span>
                      <span
                        className={
                          delta >= 0
                            ? "admin-text-success"
                            : "admin-text-danger"
                        }
                      >
                        {delta >= 0 ? "+" : ""}
                        {delta}%
                      </span>
                    </div>
                    <div className="adminImpact-qolBars">
                      <span className="admin-muted adminImpact-qolLabel">
                        B
                      </span>
                      <Progress value={item.baseline} tone="primary" />
                      <span className="admin-muted adminImpact-qolPct">
                        {item.baseline}%
                      </span>
                    </div>
                    <div className="adminImpact-qolBars">
                      <span className="admin-muted adminImpact-qolLabel">
                        M
                      </span>
                      <Progress value={item.midline} tone="success" />
                      <span className="admin-muted adminImpact-qolPct">
                        {item.midline}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
